// Obligations / Cost-Recovery ledger panel for the loan financials surface.
// Shows, per obligation, the original amount, the recovered-to-date (Σ
// non-reversed disbursements), the remaining balance, the settlement channel,
// the type, the waterfall priority and the state. The money engine produces
// these rows; the ONE mutation here is the PR-2 guided waive (per-row action,
// self-gated on Obligation.Waive) which resolves BOTH representations —
// obligation + linked fee — through WaiveObligationModal.

import { useState } from "react";
import {
  Button,
  Card,
  EmptyState,
  SkeletonList,
  StatusBadge,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
  type StatusTone,
} from "@/design-system/components/ui";
import { formatCurrency } from "@/design-system/lib/format";
import { useAuth } from "@/hooks/useAuth";
import type {
  ObligationLedgerRowDto,
  ObligationState,
  SettlementChannel,
} from "@/api/portfolioAdmin/obligationLedgerApi";
import { useLoanObligations } from "@/hooks/portfolioAdmin/useObligationLedger";
import { WaiveObligationModal } from "./WaiveObligationModal";
import { ObligationDetailDrawer } from "./ObligationDetailDrawer";

/** States the guided waive can act on — terminal states (paid_in_full,
 *  waived, charged_off, superseded) refuse server-side, so no action shows. */
const WAIVABLE_STATES = new Set<ObligationState>(["pending", "partially_paid"]);

const STATE_LABEL: Record<ObligationState, string> = {
  pending: "Pending",
  partially_paid: "Partially paid",
  paid_in_full: "Paid in full",
  waived: "Waived",
  charged_off: "Charged off",
  superseded: "Superseded",
};

const STATE_TONE: Record<ObligationState, StatusTone> = {
  pending: "warn",
  partially_paid: "progress",
  paid_in_full: "ok",
  waived: "muted",
  charged_off: "danger",
  superseded: "neutral",
};

const CHANNEL_LABEL: Record<SettlementChannel, string> = {
  servicer: "Servicer",
  phoenix: "Phoenix",
};

export function stateLabel(state: ObligationState): string {
  return STATE_LABEL[state] ?? state;
}

export function stateTone(state: ObligationState): StatusTone {
  return STATE_TONE[state] ?? "neutral";
}

export function channelLabel(channel: SettlementChannel | null): string {
  return channel ? (CHANNEL_LABEL[channel] ?? channel) : "—";
}

export function LoanObligationsPanel({ loanId }: { loanId: string }) {
  const query = useLoanObligations(loanId);
  const { hasPermission } = useAuth();
  const canWaive = hasPermission("Obligation.Waive");
  const [waiving, setWaiving] = useState<ObligationLedgerRowDto | null>(null);
  const [detailing, setDetailing] = useState<ObligationLedgerRowDto | null>(null);

  if (query.isLoading) {
    return (
      <Card data-testid="loan-obligations-loading">
        <SkeletonList rows={4} />
      </Card>
    );
  }

  if (query.isError || !query.data) {
    return (
      <Card data-testid="loan-obligations-error">
        <p className="text-small text-status-error m-0 mb-space-2">
          Obligations could not be loaded.
        </p>
        <Button variant="secondary" size="sm" onClick={() => void query.refetch()}>
          Retry
        </Button>
      </Card>
    );
  }

  const { obligations, summary } = query.data;

  return (
    <Card data-testid="loan-obligations-panel">
      <div className="flex items-start justify-between gap-space-3 mb-space-3">
        <h3 className="font-display font-bold text-h3 m-0">Obligations & cost recovery</h3>
        <div className="flex items-center gap-space-4 text-small text-gray-mid">
          <SummaryStat label="Original" value={summary.totalOriginal} />
          <SummaryStat label="Recovered" value={summary.totalRecovered} />
          <SummaryStat label="Remaining" value={summary.totalRemaining} />
        </div>
      </div>

      {obligations.length === 0 ? (
        <EmptyState
          title="No obligations yet"
          description="Cost-recovery obligations appear here as the payment engine creates and retires them."
        />
      ) : (
        <Table>
          <THead>
            <TR>
              <TH>Type</TH>
              <TH className="text-right">Priority</TH>
              <TH>State</TH>
              <TH className="text-right">Original</TH>
              <TH className="text-right">Recovered</TH>
              <TH className="text-right">Remaining</TH>
              <TH>Settlement</TH>
              {canWaive && <TH className="text-right">Actions</TH>}
            </TR>
          </THead>
          <TBody>
            {obligations.map((o) => (
              <ObligationRow
                key={o.obligationId}
                row={o}
                canWaive={canWaive}
                onWaive={() => setWaiving(o)}
                onDetail={() => setDetailing(o)}
              />
            ))}
          </TBody>
        </Table>
      )}

      {waiving && (
        <WaiveObligationModal
          loanId={loanId}
          row={waiving}
          onClose={() => setWaiving(null)}
        />
      )}

      {detailing && (
        <ObligationDetailDrawer
          loanId={loanId}
          obligationId={detailing.obligationId}
          onClose={() => setDetailing(null)}
        />
      )}
    </Card>
  );
}

function ObligationRow({
  row,
  canWaive,
  onWaive,
  onDetail,
}: {
  row: ObligationLedgerRowDto;
  canWaive: boolean;
  onWaive: () => void;
  onDetail: () => void;
}) {
  return (
    <TR data-testid="loan-obligation-row">
      <TD>
        <button
          type="button"
          className="font-medium text-left text-gold-shadow hover:underline"
          onClick={onDetail}
          data-testid={`obligation-detail-${row.obligationId}`}
        >
          {row.typeDisplay || row.typeCode}
        </button>
        {row.typeDisplay && (
          <span className="block text-caption text-gray-mid">{row.typeCode}</span>
        )}
      </TD>
      <TD className="text-right tabular-nums">{row.waterfallPriority ?? "—"}</TD>
      <TD>
        <StatusBadge tone={stateTone(row.state)}>{stateLabel(row.state)}</StatusBadge>
      </TD>
      <TD className="text-right tabular-nums">{formatCurrency(row.originalAmount)}</TD>
      <TD className="text-right tabular-nums">{formatCurrency(row.recoveredToDate)}</TD>
      <TD className="text-right tabular-nums">{formatCurrency(row.remaining)}</TD>
      <TD>{channelLabel(row.settlementChannel)}</TD>
      {canWaive && (
        <TD className="text-right whitespace-nowrap">
          {WAIVABLE_STATES.has(row.state) && (
            <button
              type="button"
              className="text-small text-gold-shadow hover:underline"
              onClick={onWaive}
              data-testid={`obligation-waive-${row.obligationId}`}
            >
              Waive…
            </button>
          )}
        </TD>
      )}
    </TR>
  );
}

function SummaryStat({ label, value }: { label: string; value: number }) {
  return (
    <span className="flex flex-col items-end">
      <span className="text-caption uppercase tracking-label text-gray-mid">{label}</span>
      <span className="font-display font-bold text-onyx tabular-nums">
        {formatCurrency(value)}
      </span>
    </span>
  );
}
